import { useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Phone, Loader2, CheckCircle, Shield, AlertTriangle, MessageCircle } from "lucide-react";
import { CRM_BRIDGE_VERSION } from "@/lib/submitWebsiteLeadForm";
import { toast } from "sonner";
import { useLanguage } from "@/contexts/LanguageContext";
import { triggerWhatsAppAutomation } from "@/hooks/useWhatsAppAutomation";
import { TurnstileWidget } from "@/components/TurnstileWidget";
import { formMessages } from "@/lib/formMessages";
import { clinicOsDuplicateMessage } from "@/lib/clinicOsIntake";
import { submitSignalLead, runNonBlocking, clinicOsUnreachableMessage } from "@/lib/submitSignalLead";

interface QuickCallbackFormProps {
  variant?: "hero" | "floating" | "inline";
  className?: string;
}

const WHATSAPP_NUMBER = "31638523131";
const WHATSAPP_DISPLAY = "+31 6 38523131";

export const QuickCallbackForm = ({ variant = "inline", className = "" }: QuickCallbackFormProps) => {
  const [phone, setPhone] = useState("");
  const honeypotRef = useRef<HTMLInputElement>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [turnstileFailed, setTurnstileFailed] = useState(false);
  const { language } = useLanguage();

  const texts = {
    tr: {
      placeholder: "Telefon numaranız",
      cta: "Beni Arayın",
      submitting: "Gönderiliyor...",
      success: "En kısa sürede sizi arayacağız!",
      error: "Bir hata oluştu. Tekrar deneyin.",
      verifyRequired: "Lütfen önce doğrulamayı tamamlayın.",
      verifying: "Güvenlik doğrulaması yükleniyor...",
      verifyFailed: "Güvenlik doğrulaması yüklenemedi. Lütfen WhatsApp ile ulaşın:",
      whatsappCta: "WhatsApp ile yaz",
    },
    en: {
      placeholder: "Your phone number",
      cta: "Call Me Back",
      submitting: "Submitting...",
      success: "We'll call you back shortly!",
      error: "An error occurred. Please try again.",
      verifyRequired: "Please complete verification first.",
      verifying: "Loading security check...",
      verifyFailed: "Security check couldn't load. Please contact us on WhatsApp:",
      whatsappCta: "Message on WhatsApp",
    },
    fr: {
      placeholder: "Votre numéro de téléphone",
      cta: "Rappelez-moi",
      submitting: "Envoi en cours...",
      success: "Nous vous rappellerons bientôt!",
      error: "Une erreur s'est produite. Veuillez réessayer.",
      verifyRequired: "Veuillez d'abord compléter la vérification.",
      verifying: "Chargement de la vérification...",
      verifyFailed: "La vérification n'a pas pu se charger. Contactez-nous sur WhatsApp:",
      whatsappCta: "Message WhatsApp",
    },
    nl: {
      placeholder: "Uw telefoonnummer",
      cta: "Bel mij terug",
      submitting: "Verzenden...",
      success: "We bellen u zo snel mogelijk terug!",
      error: "Er is een fout opgetreden. Probeer het opnieuw.",
      verifyRequired: "Voltooi eerst de verificatie.",
      verifying: "Beveiligingscontrole laden...",
      verifyFailed: "Beveiligingscontrole kon niet laden. Neem contact op via WhatsApp:",
      whatsappCta: "Bericht via WhatsApp",
    },
    ar: {
      placeholder: "رقم هاتفك",
      cta: "اتصل بي",
      submitting: "جاري الإرسال...",
      success: "سنتصل بك قريباً!",
      error: "حدث خطأ. حاول مرة أخرى.",
      verifyRequired: "يرجى إكمال التحقق أولاً.",
      verifying: "جارٍ تحميل التحقق الأمني...",
      verifyFailed: "تعذر تحميل التحقق. تواصل معنا عبر واتساب:",
      whatsappCta: "راسلنا على واتساب",
    },
  };

  const t = texts[language as keyof typeof texts] || texts.en;
  const whatsappHref = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(
    (phone ? `${phone} - ` : "") + t.cta
  )}`;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!phone || phone.replace(/\D/g, "").length < 8) {
      toast.error(formMessages.invalidPhone(language));
      return;
    }

    if (!turnstileToken || turnstileFailed) {
      toast.error(turnstileFailed ? t.verifyFailed : t.verifyRequired);
      return;
    }

    setIsSubmitting(true);

    try {
      const sourcePage = typeof window !== "undefined" ? window.location.pathname : "/";

      console.info("[signal-lead] quick_callback_submit_started", { source: "Signal Website - Quick Callback", source_page: sourcePage });
      const outcome = await submitSignalLead({
        name: "Quick Callback - Signal",
        phone,
        language,
        source: "Signal Website - Quick Callback",
        source_page: sourcePage,
        message: "Quick callback request",
        website: honeypotRef.current?.value || "",
        turnstile_token: turnstileToken,
      });

      console.info(
        outcome.status === "success"
          ? "[signal-lead] quick_callback_submit_success"
          : "[signal-lead] quick_callback_submit_failed",
        {
          status: outcome.status,
          duplicate: outcome.status === "success" ? outcome.duplicate : undefined,
          errorCode: outcome.status === "unreachable" ? outcome.errorCode : undefined,
        },
      );

      if (outcome.status !== "success") {
        toast.error(clinicOsUnreachableMessage(language));
        setIsSubmitting(false);
        return;
      }

      runNonBlocking("whatsapp automation", async () => {
        triggerWhatsAppAutomation({
          trigger_type: "callback_request",
          phone,
        });
      });

      setIsSubmitted(true);
      toast.success(outcome.duplicate ? clinicOsDuplicateMessage(language) : t.success);

      setTimeout(() => {
        setIsSubmitted(false);
        setPhone("");
        setTurnstileToken(null);
      }, 5000);
    } catch (error) {
      console.error("Quick callback error:", error);
      toast.error(clinicOsUnreachableMessage(language));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className={`flex items-center gap-2 ${variant === "hero" ? "text-white" : "text-foreground"} ${className}`}>
        <CheckCircle className="w-5 h-5 text-green-500" />
        <span className="font-medium">{t.success}</span>
      </div>
    );
  }

  // Button is only truly disabled while submitting. When Turnstile isn't ready,
  // the user gets a clear inline message on click instead of a mysteriously
  // dead button.
  const buttonDisabled = isSubmitting;
  const showVerifyHint = !turnstileToken && !turnstileFailed;

  if (variant === "hero") {
    return (
      <div className={`space-y-3 ${className}`}>
        <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3" data-crm-bridge={CRM_BRIDGE_VERSION} data-form-source="quick-callback-hero">
              {/* Honeypot - hidden from real users */}
              <input ref={honeypotRef} type="text" name="website" tabIndex={-1} autoComplete="off" aria-hidden="true" className="hidden" />

          <div className="relative flex-1">
            <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/70" />
            <Input
              type="tel"
              inputMode="tel"
              autoComplete="tel"
              placeholder={t.placeholder}
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              className="pl-10 h-14 text-lg bg-white/10 border-white/30 text-white placeholder:text-white/60 focus:bg-white/20"
              aria-label={t.placeholder}
            />
          </div>
          <Button
            type="submit"
            size="lg"
            disabled={buttonDisabled}
            className="h-14 px-8 bg-green-500 hover:bg-green-600 text-white font-bold text-lg shadow-lg hover:shadow-xl transition-all disabled:opacity-80"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                {t.submitting}
              </>
            ) : (
              <>
                <Phone className="w-5 h-5 mr-2" />
                {t.cta}
              </>
            )}
          </Button>
        </form>

        {!turnstileFailed && (
          <div className="flex flex-col items-center gap-2">
            <TurnstileWidget
              onVerify={(token) => {
                setTurnstileToken(token);
                setTurnstileFailed(false);
              }}
              onExpired={() => setTurnstileToken(null)}
              onError={() => {
                setTurnstileFailed(true);
                setTurnstileToken(null);
              }}
              theme="dark"
              size="compact"
            />
            {showVerifyHint && (
              <p className="text-xs text-white/80 flex items-center gap-1">
                <Shield className="w-3 h-3" />
                {t.verifying}
              </p>
            )}
          </div>
        )}

        {turnstileFailed && (
          <div className="rounded-md bg-white/10 border border-white/30 p-3 text-sm text-white flex flex-col sm:flex-row sm:items-center gap-2">
            <AlertTriangle className="w-4 h-4 shrink-0" />
            <span className="flex-1">{t.verifyFailed}</span>
            <a
              href={whatsappHref}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 rounded bg-green-500 hover:bg-green-600 px-3 py-1.5 font-medium"
            >
              <MessageCircle className="w-4 h-4" />
              {t.whatsappCta} {WHATSAPP_DISPLAY}
            </a>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={`space-y-2 ${className}`}>
      <form onSubmit={handleSubmit} className="flex items-center gap-2" data-crm-bridge={CRM_BRIDGE_VERSION} data-form-source="quick-callback-inline">
              {/* Honeypot - hidden from real users */}
              <input ref={honeypotRef} type="text" name="website" tabIndex={-1} autoComplete="off" aria-hidden="true" className="hidden" />

        <Input
          type="tel"
          inputMode="tel"
          autoComplete="tel"
          placeholder={t.placeholder}
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          className="w-40 h-9 text-sm"
          aria-label={t.placeholder}
        />
        <Button
          type="submit"
          size="sm"
          disabled={buttonDisabled}
          className="bg-green-500 hover:bg-green-600 text-white"
        >
          {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Phone className="w-4 h-4" />}
        </Button>
      </form>

      {!turnstileFailed && (
        <>
          {showVerifyHint && (
            <div className="flex items-center gap-1 text-xs text-muted-foreground">
              <Shield className="w-3 h-3" />
              <span>{t.verifying}</span>
            </div>
          )}
          <TurnstileWidget
            onVerify={(token) => {
              setTurnstileToken(token);
              setTurnstileFailed(false);
            }}
            onExpired={() => setTurnstileToken(null)}
            onError={() => {
              setTurnstileFailed(true);
              setTurnstileToken(null);
            }}
            theme="auto"
            size="compact"
          />
        </>
      )}

      {turnstileFailed && (
        <a
          href={whatsappHref}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-xs text-green-700 hover:underline"
        >
          <MessageCircle className="w-3 h-3" />
          {t.whatsappCta}
        </a>
      )}
    </div>
  );
};
